<template>
  <div v-if="visible" class="stats-panel">
    <div class="stats-header">
      <h3>Статистика соединения</h3>
      <button @click="emit('close')" class="close-btn" title="Закрыть статистику">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Мои метрики -->
    <div class="metrics-section">
      <h4><i class="fas fa-chart-bar"></i> Мои метрики</h4>
      <div class="stats-grid">
        <div class="stat-item">
          <span class="stat-label">Статус:</span>
          <span :class="['stat-value', connectionState]">{{ connectionStateText }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Тип соединения:</span>
          <span class="stat-value">{{ connectionType }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">RTT:</span>
          <span class="stat-value">{{ stats.rtt }} ms</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Битрейт ↑:</span>
          <span class="stat-value">{{ stats.bitrateOut }} kbps</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Битрейт ↓:</span>
          <span class="stat-value">{{ stats.bitrateIn }} kbps</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Потери пакетов:</span>
          <span class="stat-value">{{ stats.packetLoss }}%</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Качество видео:</span>
          <span class="stat-value">{{ currentQuality }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Кодек видео:</span>
          <span class="stat-value">{{ peerMetrics.codecs.video || 'N/A' }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Кодек аудио:</span>
          <span class="stat-value">{{ peerMetrics.codecs.audio || 'N/A' }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Разрешение:</span>
          <span class="stat-value">{{ peerMetrics.resolution.width }}x{{ peerMetrics.resolution.height }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Переподключение:</span>
          <span :class="['stat-value', isReconnecting ? 'reconnecting' : '']">
            {{ isReconnecting ? 'Да' : 'Нет' }}
          </span>
        </div>
        <div v-if="cameraInfo" class="stat-item">
          <span class="stat-label">Камера:</span>
          <span class="stat-value">
            {{ formatCameraType(cameraInfo.type) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Метрики собеседника -->
    <div v-if="remotePeerName" class="metrics-section">
      <h4>👤 {{ remotePeerName }} (собеседник)</h4>
      <div class="stats-grid">
        <div class="stat-item">
          <span class="stat-label">Тип соединения:</span>
          <span class="stat-value">{{ remotePeerMetrics.network?.connectionType || 'N/A' }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">RTT:</span>
          <span class="stat-value">{{ remotePeerMetrics.network?.rtt || 0 }} ms</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Битрейт ↑:</span>
          <span class="stat-value">{{ remotePeerMetrics.network?.bitrateOut || 0 }} kbps</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Битрейт ↓:</span>
          <span class="stat-value">{{ remotePeerMetrics.network?.bitrateIn || 0 }} kbps</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Потери пакетов:</span>
          <span class="stat-value">{{ remotePeerMetrics.network?.packetLoss || 0 }}%</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Качество видео:</span>
          <span class="stat-value">{{ remotePeerMetrics.quality || 'N/A' }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Кодек видео:</span>
          <span class="stat-value">{{ remotePeerMetrics.codecs?.video || 'N/A' }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Кодек аудио:</span>
          <span class="stat-value">{{ remotePeerMetrics.codecs?.audio || 'N/A' }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Разрешение:</span>
          <span class="stat-value">{{ remotePeerMetrics.resolution?.width || 0 }}x{{ remotePeerMetrics.resolution?.height || 0 }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Микрофон:</span>
          <span :class="['stat-value', remotePeerMetrics.media?.audioEnabled ? 'enabled' : 'disabled']">
            {{ remotePeerMetrics.media?.audioEnabled ? 'Включен' : 'Выключен' }}
          </span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Камера:</span>
          <span :class="['stat-value', remotePeerMetrics.media?.videoEnabled ? 'enabled' : 'disabled']">
            {{ remotePeerMetrics.media?.videoEnabled ? 'Включена' : 'Выключена' }}
          </span>
        </div>
        <div v-if="remotePeerMetrics.camera" class="stat-item">
          <span class="stat-label">Тип камеры:</span>
          <span class="stat-value">
            {{ formatCameraType(remotePeerMetrics.camera?.type || 'unknown') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Компонент панели статистики
 * Отображает метрики соединения для текущего пользователя и собеседника
 */

import { computed } from 'vue';
import type {
  ConnectionState,
  ConnectionStats,
  ConnectionType,
  PeerMetrics,
  QualityLevel
} from '../../types';
import { formatConnectionState, formatCameraType } from '../../utils/connectionState';

// ==================== PROPS ====================

interface Props {
  /** Видимость панели */
  visible: boolean;
  /** Состояние соединения */
  connectionState: ConnectionState;
  /** Тип соединения */
  connectionType: ConnectionType;
  /** Статистика соединения */
  stats: ConnectionStats;
  /** Текущее качество */
  currentQuality: QualityLevel;
  /** Локальные метрики */
  peerMetrics: PeerMetrics;
  /** Метрики собеседника */
  remotePeerMetrics: PeerMetrics;
  /** Имя собеседника */
  remotePeerName: string;
  /** Флаг переподключения */
  isReconnecting: boolean;
  /** Информация о камере */
  cameraInfo?: { type: 'front' | 'back' | 'unknown' } | null;
}

const props = defineProps<Props>();

// ==================== EMITS ====================

const emit = defineEmits<{
  'close': [];
}>();

// ==================== COMPUTED ====================

/** Текстовое представление состояния соединения */
const connectionStateText = computed(() => 
  formatConnectionState(props.connectionState, props.isReconnecting)
);
</script>

<style scoped>
.stats-panel {
  position: fixed;
  top: 24px;
  right: 24px;
  width: 340px;
  padding: 24px;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  z-index: 100;
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.stats-panel h3 {
  margin: 0;
  font-size: 1rem;
}

.close-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  transform: scale(1.05);
}

.stats-grid {
  display: grid;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  font-size: 0.575rem;
  padding: 0.125rem 0;
}

.stat-label {
  color: rgba(255, 255, 255, 0.7);
}

.stat-value {
  font-weight: 600;
}

.stat-value.connected {
  color: #86efac;
}

.stat-value.connecting {
  color: #fbbf24;
}

.stat-value.failed,
.stat-value.disconnected {
  color: #fca5a5;
}

.stat-value.reconnecting {
  color: #fbbf24;
}

.stat-value.enabled {
  color: #86efac;
}

.stat-value.disabled {
  color: #fca5a5;
}

.metrics-section {
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.metrics-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.metrics-section h4 {
  margin: 0 0 0.5rem 0;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;
}

/* Мобильная адаптация */
@media (max-width: 768px) {
  .stats-panel {
    width: calc(100% - 20px);
    left: 10px;
    right: 10px;
    top: 10px;
    padding: 16px;
    max-height: calc(100dvh - 20px);
    overflow-y: auto;
  }

  .stats-header {
    margin-bottom: 0.75rem;
  }

  .stat-item {
    font-size: 0.6rem;
    padding: 0.125rem 0;
  }

  .metrics-section {
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
  }

  .metrics-section h4 {
    margin: 0 0 0.375rem 0;
    font-size: 0.8rem;
  }
}
</style>
